import * as React from 'react'
import { twMerge } from 'tailwind-merge'

import { Dialog as DialogPrimitive } from 'radix-ui'

import { ModalContainer } from './ModalContainer'
import { ModalHeader } from './ModalHeader'

/** Radix Dialog root; pair with ModalTrigger and ModalContent (see modal stories). */
export const Modal = DialogPrimitive.Root

/** Radix DialogTrigger; typically wraps the control that opens the dialog. */
export const ModalTrigger = DialogPrimitive.Trigger

/**
 * Radix DialogTitle with the default modal title styling baked in; use for accessible titles
 * when not using ModalContent's string title prop. Pass `className` to extend or override.
 */
export const ModalTitle = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Title>, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Title ref={ref} className={twMerge('font-rethink-sans text-xl font-semibold leading-7 text-text-primary', className)} {...props} />
  )
)
ModalTitle.displayName = 'ModalTitle'

/**
 * ModalClose is a wrapper around Radix UI's DialogClose component.
 * Useful for creating custom close triggers within the modal.
 */
export const ModalClose = DialogPrimitive.Close

/**
 * Radix DialogDescription; renders as `<p>` and auto-wires `aria-describedby` on the dialog
 * content element. Use when not using ModalContent's `subtitle` prop (e.g. inside ModalContainer).
 * Pass `className` to style; pass `asChild` to render a custom element instead of `<p>`.
 */
export const ModalDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ ...props }, ref) => <DialogPrimitive.Description ref={ref} {...props} />)
ModalDescription.displayName = 'ModalDescription'

export interface ModalContentProps extends React.ComponentPropsWithoutRef<typeof ModalContainer> {
  /** Title of the modal */
  title?: string
  /** Alignment of the title. Default: 'left' */
  titleAlign?: 'left' | 'center'
  /** Icon to display in the title. Default: undefined */
  titleIcon?: React.ReactNode
  /** Subtitle of the modal */
  subtitle?: string
  /** Optional classes for subtitle variants defined by a consuming modal. */
  subtitleClassName?: string
  /** Show the close button in the header. Default: false */
  showCloseButton?: boolean
  /** Optional footer content rendered below the body */
  footer?: React.ReactNode
  /** Optional className for the scrollable body area */
  bodyClassName?: string
}

/** Radix DialogContent with title, optional footer, and size presets; see Modal.stories. */
export function ModalContent({
  bodyClassName,
  children,
  footer,
  height = 'md',
  preventDismiss = false,
  showCloseButton = false,
  stacked = false,
  subtitle,
  subtitleClassName,
  title,
  titleAlign = 'left',
  titleIcon,
  width = 'md',
  ...props
}: ModalContentProps) {
  const hasHeader = Boolean(title || subtitle || showCloseButton)

  return (
    <ModalContainer
      {...props}
      {...(!subtitle && { 'aria-describedby': undefined })}
      data-ds-component="Modal"
      width={width}
      height={height}
      stacked={stacked}
      preventDismiss={preventDismiss}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col p-6">
        {hasHeader ? (
          <div className="shrink-0 pb-4">
            <ModalHeader
              showCloseButton={showCloseButton}
              subtitle={subtitle}
              subtitleClassName={subtitleClassName}
              title={title}
              titleAlign={titleAlign}
              titleIcon={titleIcon}
            />
          </div>
        ) : null}
        <div className={twMerge('min-h-0 flex-1 overflow-auto', bodyClassName)}>{children}</div>
        {footer && <div className="mt-auto shrink-0 pt-6">{footer}</div>}
      </div>
    </ModalContainer>
  )
}
